/**
 * PrefetchRangeCalculator - Utility for calculating prefetch page ranges
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for calculating prefetch ranges when all
 * requested pages are already cached, enabling background prefetching to prevent
 * jank when scrolling to uncached pages.
 */

/**
 * Parameters for calculating prefetch range
 */
export interface PrefetchRangeParams {
  endPage: number;
  lookahead: number;
  maxPages: number; // Maximum page limit (e.g., 100)
  prefetchCount: number; // Number of pages to prefetch ahead (default: 2)
}

/**
 * Result of prefetch range calculation
 */
export interface PrefetchRangeResult {
  shouldPrefetch: boolean;
  prefetchStart: number | null;
  prefetchEnd: number | null;
}

/**
 * Calculate prefetch range for background pagination
 * 
 * Determines if prefetching should occur and calculates the range of pages
 * to prefetch when all requested pages are already cached.
 * 
 * Prefetching only occurs when:
 * - All requested pages are cached (neededPages.length === 0, checked by caller)
 * - The end page + lookahead is below the maximum page limit
 * 
 * @param params - Parameters for prefetch range calculation
 * @returns Object indicating if prefetch should occur and the range
 */
export function calculatePrefetchRange(params: PrefetchRangeParams): PrefetchRangeResult {
  const { endPage, lookahead, maxPages, prefetchCount = 2 } = params;
  
  // Don't prefetch if we're already near the maximum page limit
  if (endPage + lookahead >= maxPages) {
    return {
      shouldPrefetch: false,
      prefetchStart: null,
      prefetchEnd: null
    };
  }
  
  // Calculate prefetch range: start from endPage + lookahead + 1, prefetch prefetchCount pages
  const prefetchStart = endPage + lookahead + 1;
  const prefetchEnd = Math.min(prefetchStart + prefetchCount, maxPages);
  
  return {
    shouldPrefetch: true,
    prefetchStart,
    prefetchEnd
  };
}

/**
 * Generate prefetch range key for checking if prefetch is already in flight
 * 
 * Uses the same key generation logic as pagination range keys to ensure
 * consistent deduplication of in-flight requests.
 * 
 * @param docHash - Document hash
 * @param prefetchStart - Starting page index for prefetch
 * @param prefetchEnd - Ending page index for prefetch
 * @param wasmFontUid - Font UID for cache key
 * @param fontSizePx - Font size in pixels for cache key
 * @param generateRangeKey - Function to generate pagination range key
 * @returns Prefetch range key string
 */
export function generatePrefetchRangeKey(
  docHash: string,
  prefetchStart: number,
  prefetchEnd: number,
  wasmFontUid: string,
  fontSizePx: number,
  generateRangeKey: (docHash: string, startPage: number, endPage: number, fontUid: string, fontSizePx: number) => string
): string {
  return generateRangeKey(docHash, prefetchStart, prefetchEnd, wasmFontUid, fontSizePx);
}

